import axios from "axios";
import { useMerchantStore } from "../store/useMerchantStore";
import { t } from "../i18n";

function computeBaseUrl() {
  const envBase = String(process.env.REACT_APP_API_BASE_URL || "").trim();
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    const h = String(window.location.hostname || "").toLowerCase();
    // CRA dev / local docker SPA: always same-origin so /api proxy is the single backend.
    // A non-empty REACT_APP_API_BASE_URL (e.g. prod URL baked into a local image) would send
    // registration to remote while KYC admin fetches used origin - different DBs, empty list.
    if (h === "localhost" || h === "127.0.0.1") {
      return origin;
    }
    // On HTTPS pages, never use an insecure http:// API base from env.
    if (window.location.protocol === "https:" && envBase.startsWith("http://")) {
      return origin;
    }
    if (!envBase) return origin;
  }
  const raw =
    envBase || (typeof window !== "undefined" ? window.location.origin : "http://localhost:8080");
  // Strip any path on env (e.g. …/api) so axios paths stay /api/… only - avoids /api/api/… and Spring 401 on login.
  try {
    return new URL(raw).origin;
  } catch {
    return String(raw).replace(/\/$/, "");
  }
}

const baseURL = computeBaseUrl();
const TOKEN_KEY = "odos_merchant_token";

/** Use for raw fetch() calls; axios already uses baseURL. Relative paths hit the SPA origin in production. */
export function apiUrl(pathOrUrl) {
  if (pathOrUrl == null) return pathOrUrl;
  const s = String(pathOrUrl).trim();
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const p = s.startsWith("/") ? s : `/${s}`;
  const base = String(baseURL || "").replace(/\/$/, "");
  return `${base}${p}`;
}

export const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth endpoints legitimately return 401 on bad credentials - don't treat those as an expired
// session, or a failed login attempt would force-redirect instead of showing the error inline.
const AUTH_EXEMPT_URL_FRAGMENTS = ["/login", "/register", "/forgot-password", "/email-otp"];

function isAuthExemptUrl(url) {
  const u = String(url || "");
  return AUTH_EXEMPT_URL_FRAGMENTS.some((f) => u.includes(f));
}

/** Clears the merchant session and hard-redirects to /login so an expired session doesn't just leave the page stuck on an error. */
function handleMerchantSessionExpired() {
  setMerchantToken(null);
  useMerchantStore.getState().clearSession();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401 && !isAuthExemptUrl(error?.config?.url)) {
      handleMerchantSessionExpired();
    }
    return Promise.reject(error);
  }
);

export function getMerchantToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setMerchantToken(token) {
  if (!token) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, token);
}

export async function health() {
  const res = await api.get("/api/health");
  return res.data;
}

export async function listPublicMerchants() {
  const res = await api.get("/api/public/merchants");
  return res.data;
}

/** Public list of `{ code, label }` for registration / portal profile. */
export async function listMerchantBusinessCategories() {
  const res = await fetch(apiUrl("/api/public/merchant-business-categories"));
  const data = await res.json().catch(() => []);
  if (!res.ok) {
    throw new Error(typeof data === "object" && data?.message ? data.message : `HTTP ${res.status}`);
  }
  return Array.isArray(data) ? data : [];
}

export async function getMerchantProfile(merchantId) {
  const res = await api.get(`/api/merchants/${merchantId}`);
  return res.data;
}

export async function patchMerchantPortalProfile(merchantId, body) {
  const res = await api.patch(`/api/merchants/${merchantId}/portal-profile`, body);
  return res.data;
}

/** Default payer (SENDER or RECEIVER) of the driver's delivery fee for the merchant's new orders. */
export async function setDefaultDeliveryFeePayer(merchantId, payer) {
  const res = await api.put(`/api/merchants/${merchantId}/delivery-fee-payer`, { deliveryFeePayer: payer });
  return res.data;
}

/** Who pays the driver what on a route (merchant at pickup, receivers at the door), incl. per-stop rows. */
export async function getRouteSettlement(routeId) {
  const res = await api.get(`/api/orders/routes/${routeId}/settlement`);
  return res.data;
}

/** payer: SENDER or RECEIVER for orderIds, or every stop when orderIds is empty. Allowed until pickup. */
export async function setRouteDeliveryFeePayer(routeId, payer, orderIds) {
  const body = { deliveryFeePayer: payer };
  if (Array.isArray(orderIds) && orderIds.length > 0) body.orderIds = orderIds;
  const res = await api.put(`/api/orders/routes/${routeId}/delivery-fee-payer`, body);
  return res.data;
}

export async function uploadMerchantProfileImage(merchantId, file) {
  if (!(file instanceof File)) throw new Error("file required");
  const fd = new FormData();
  fd.append("file", file);
  const token = getMerchantToken();
  const res = await fetch(apiUrl(`/api/merchants/${merchantId}/profile-image/upload`), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) handleMerchantSessionExpired();
    const err = new Error(typeof data.message === "string" ? data.message : `HTTP ${res.status}`);
    err.response = { status: res.status, data };
    throw err;
  }
  return data?.path ?? null;
}

/**
 * Merchant portal login: `password` (BCrypt per merchant). Legacy `accessCode` only if server enables migration flag.
 */
export async function merchantLogin({ merchantId, contactEmail, password, accessCode }) {
  const body = {};
  const ce = contactEmail != null ? String(contactEmail).trim() : "";
  if (ce) {
    body.contactEmail = ce;
    body.contact_email = ce;
  } else if (merchantId != null && merchantId !== "") {
    body.merchantId = merchantId;
    body.merchant_id = merchantId;
  }
  if (password != null && String(password).length > 0) {
    body.password = String(password);
  }
  if (accessCode != null && String(accessCode).trim()) {
    const code = String(accessCode).trim();
    body.accessCode = code;
    body.access_code = code;
  }
  const res = await api.post("/api/merchants/login", body);
  const token = res?.data?.token;
  if (token) setMerchantToken(token);
  return res.data;
}

/** Sends a one-time code to a prospective merchant's contact email; must succeed before {@link registerMerchant}. */
export async function requestMerchantEmailOtp(contactEmail) {
  const res = await api.post("/api/public/merchants/email-otp/request", {
    contactEmail: String(contactEmail || "").trim(),
  });
  return res.data;
}

/** Verifies the code sent by {@link requestMerchantEmailOtp}. */
export async function verifyMerchantEmailOtp(contactEmail, otp) {
  const res = await api.post("/api/public/merchants/email-otp/verify", {
    contactEmail: String(contactEmail || "").trim(),
    otp: String(otp || "").trim(),
  });
  return res.data;
}

/**
 * Self-service registration (public; no JWT). Multipart: JSON `merchant`, storefront `profileImage`, and compliance
 * PDFs `tinDocumentPdf`, `businessRegistrationPdf`, `businessLicensePdf` (each max 10 MB, PDF-only on server).
 */
export async function registerMerchant(
  payload,
  profileImageFile,
  tinDocumentPdf,
  businessRegistrationPdf,
  businessLicensePdf
) {
  if (!(profileImageFile instanceof File)) {
    const err = new Error(t("reg.err_photo"));
    err.response = { status: 400, data: { message: err.message } };
    throw err;
  }
  if (!(tinDocumentPdf instanceof File)) {
    const err = new Error(t("reg.err_tin_pdf"));
    err.response = { status: 400, data: { message: err.message } };
    throw err;
  }
  if (!(businessRegistrationPdf instanceof File)) {
    const err = new Error(t("reg.err_registration_pdf"));
    err.response = { status: 400, data: { message: err.message } };
    throw err;
  }
  if (!(businessLicensePdf instanceof File)) {
    const err = new Error(t("reg.err_license_pdf"));
    err.response = { status: 400, data: { message: err.message } };
    throw err;
  }
  const fd = new FormData();
  fd.append("merchant", new Blob([JSON.stringify(payload)], { type: "application/json" }));
  fd.append("profileImage", profileImageFile);
  fd.append("tinDocumentPdf", tinDocumentPdf);
  fd.append("businessRegistrationPdf", businessRegistrationPdf);
  fd.append("businessLicensePdf", businessLicensePdf);
  const res = await fetch(apiUrl("/api/public/merchants/register"), {
    method: "POST",
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(typeof data.message === "string" ? data.message : `HTTP ${res.status}`);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

export async function createMerchant(data) {
  const res = await api.post("/api/merchants", typeof data === "string" ? { name: data } : data);
  return res.data;
}

export async function listOrdersByMerchant(merchantId, status = null) {
  const params = { merchantId };
  if (status != null && status !== "") params.status = status;
  const res = await api.get("/api/orders", { params });
  return res.data;
}

export async function initiateRouteOptimization(merchantId, payload) {
  const res = await api.post("/api/orders/route-optimization", payload);
  return res.data;
}

/** `{ code, title, description }[]`- same catalog as odos_client `fetchPublicVehicleTypes`. */
export async function fetchPublicVehicleTypes() {
  const res = await fetch(apiUrl("/api/public/vehicle-types"));
  if (!res.ok) {
    const t = await res.text();
    throw new Error((t && t.slice(0, 200)) || `HTTP ${res.status}`);
  }
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}

/** `{ code, title, description }[]`- optional route capability constraints (cold chain, etc.). */
export async function fetchPublicVehicleStandards() {
  const res = await fetch(apiUrl("/api/public/vehicle-standards"));
  if (!res.ok) {
    const t = await res.text();
    throw new Error((t && t.slice(0, 200)) || `HTTP ${res.status}`);
  }
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}

/** ODOS platform service fee preview (same breakdown as client app). */
export async function getRouteCostPreview(routeId) {
  const res = await api.get(`/api/orders/routes/${routeId}/cost-preview`);
  return res.data;
}

/** Chapa: returns `{ checkoutUrl, txRef, amountEtb }`. */
export async function initiateRouteChapaPayment(routeId, returnUrl) {
  const body =
    returnUrl != null && String(returnUrl).trim() !== ""
      ? { returnUrl: String(returnUrl).trim() }
      : {};
  const res = await api.post(`/api/orders/routes/${routeId}/payment/initiate`, body);
  return res.data;
}

export async function verifyChapaRoutePayment(routeId, txRef) {
  await api.post(`/api/orders/routes/${routeId}/payment/verify-chapa`, {
    txRef: String(txRef).trim(),
  });
}

export async function createOrder(payload) {
  const res = await api.post("/api/orders", payload);
  return res.data;
}

// ---- Maps helpers (shared with odos_client behavior) ----

function parseCoordsFromMapString(text) {
  const t = String(text || "").trim();
  if (!t) return null;

  const normalized = t.replaceAll("%21", "!");

  const patterns = [
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/, // !3dLAT!4dLNG
    /maps\/search\/(-?\d+\.\d+)\s*,\s*\+?(-?\d+\.\d+)/i, // /maps/search/lat,+lng
    /@(-?\d+\.\d+),(-?\d+\.\d+)/, // @lat,lng
    /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/, // q=lat,lng
    /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/, // ll=lat,lng
    /!4d(-?\d+\.\d+)!3d(-?\d+\.\d+)/, // !4dLNG!3dLAT
  ];

  for (const re of patterns) {
    const m = normalized.match(re);
    if (m && m[1] != null && m[2] != null) {
      const a = parseFloat(m[1]);
      const b = parseFloat(m[2]);
      // Handle !4d!3d ordering
      const lat = re.source.startsWith("!4d") ? b : a;
      const lng = re.source.startsWith("!4d") ? a : b;
      if (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      ) {
        return { lat, lng };
      }
    }
  }

  // Last resort: any "lat, lng" inside a google maps text
  if (normalized.includes("google.com/maps") || normalized.includes("maps.google")) {
    const mAny = normalized.match(/[-]?\d{1,3}\.\d+,\s*[-+]?\d{1,3}\.\d+/);
    if (mAny) {
      const parts = mAny[0].split(",");
      if (parts.length === 2) {
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim().replace(/^\+/, ""));
        if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
      }
    }
  }
  return null;
}

function normalizeMapUrlForHttp(url) {
  let t = String(url || "").trim();
  if (!t) return t;
  if (!t.startsWith("http://") && !t.startsWith("https://")) t = `https://${t}`;
  return t;
}

export async function resolveMapUrl(url) {
  const trimmed = String(url || "").trim();
  const inline = parseCoordsFromMapString(trimmed);
  if (inline) return { ...inline, formattedAddress: "" };

  const forHttp = normalizeMapUrlForHttp(trimmed);
  const encoded = encodeURIComponent(forHttp);
  const res = await api.get(`/api/maps/resolve-url?url=${encoded}`);
  return {
    lat: Number(res.data.latitude),
    lng: Number(res.data.longitude),
    formattedAddress: String(res.data.formattedAddress || ""),
  };
}

export async function autocompleteLocation(input) {
  const encoded = encodeURIComponent(String(input || "").trim());
  const res = await api.get(`/api/maps/autocomplete?input=${encoded}`);
  return (res.data.predictions || []).filter((p) => p.placeId && p.description);
}

export async function getPlaceDetails(placeId) {
  const encoded = encodeURIComponent(String(placeId || "").trim());
  const res = await api.get(`/api/maps/place-details?placeId=${encoded}`);
  return {
    lat: Number(res.data.latitude),
    lng: Number(res.data.longitude),
    formattedAddress: String(res.data.formattedAddress || ""),
  };
}

export async function reverseGeocodeLatLng(lat, lng) {
  const la = Number(lat);
  const ln = Number(lng);
  const res = await api.get(`/api/maps/reverse-geocode?lat=${encodeURIComponent(la)}&lng=${encodeURIComponent(ln)}`);
  return {
    formattedAddress: String(res.data.formattedAddress || ""),
  };
}

export async function markItemReady(orderId, itemId, ready) {
  const res = await api.patch(`/api/orders/${orderId}/items/${itemId}/ready`, { ready });
  return res.data;
}

export async function markOrderAllItemsReady(orderId, ready) {
  const res = await api.patch(`/api/orders/${orderId}/items/mark-all-ready`, { ready });
  return res.data;
}

export async function listProducts(merchantId) {
  const res = await api.get(`/api/merchants/${merchantId}/products`);
  return res.data;
}

export async function createProduct(merchantId, data) {
  const { unit: _u, type: _t, ...rest } = data || {};
  const res = await api.post("/api/products", {
    merchantId,
    unit: "-",
    type: "-",
    ...rest,
  });
  return res.data;
}

export async function updateProduct(id, data) {
  const { unit: _u, type: _t, ...rest } = data || {};
  const res = await api.put(`/api/products/${id}`, {
    unit: "-",
    type: "-",
    ...rest,
  });
  return res.data;
}

export async function listProductFieldDefinitions(merchantId) {
  const res = await api.get(`/api/merchants/${merchantId}/product-field-definitions`);
  return res.data;
}

export async function createProductFieldDefinition(merchantId, body) {
  const res = await api.post(`/api/merchants/${merchantId}/product-field-definitions`, body);
  return res.data;
}

export async function updateProductFieldDefinition(merchantId, id, body) {
  const res = await api.put(`/api/merchants/${merchantId}/product-field-definitions/${id}`, body);
  return res.data;
}

export async function deleteProductFieldDefinition(merchantId, id) {
  await api.delete(`/api/merchants/${merchantId}/product-field-definitions/${id}`);
}

export async function deleteProduct(id) {
  await api.delete(`/api/products/${id}`);
}

export async function uploadMerchantProductImage(merchantId, file) {
  const form = new FormData();
  form.append("file", file);
  const token = getMerchantToken();
  const headers = { "Content-Type": "multipart/form-data" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await api.post(`/api/merchants/${merchantId}/product-images/upload`, form, {
    headers,
  });
  return res.data;
}

export async function listUnits(merchantId) {
  const res = await api.get(`/api/units/merchant/${merchantId}`);
  return res.data;
}

export async function createUnit(merchantId, name) {
  const res = await api.post("/api/units", { name, merchantId });
  return res.data;
}

export async function deleteUnit(id) {
  await api.delete(`/api/units/${id}`);
}

export async function listProductTypes(merchantId) {
  const res = await api.get(`/api/product-types/merchant/${merchantId}`);
  return res.data;
}

export async function createProductType(merchantId, name) {
  const res = await api.post("/api/product-types", { name, merchantId });
  return res.data;
}

export async function deleteProductType(id) {
  await api.delete(`/api/product-types/${id}`);
}

// ---- Portal admin (KYC admin JWT, sessionStorage - separate from merchant login) ----

const ADMIN_TOKEN_SESSION_KEY = "odos_merchant_portal_admin_token";

export function getMerchantPortalAdminToken() {
  try {
    const t = sessionStorage.getItem(ADMIN_TOKEN_SESSION_KEY);
    return t != null && String(t).trim() ? String(t).trim() : null;
  } catch {
    return null;
  }
}

export function setMerchantPortalAdminToken(token) {
  try {
    if (!token) sessionStorage.removeItem(ADMIN_TOKEN_SESSION_KEY);
    else sessionStorage.setItem(ADMIN_TOKEN_SESSION_KEY, String(token).trim());
  } catch {
    /* ignore */
  }
}

/** Admin (KYC JWT) URLs; same base as merchant API (computeBaseUrl forces localhost → SPA origin). */
function resolveMerchantPortalAdminRequestUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  return apiUrl(p);
}

export async function merchantPortalKycAdminLogin(username, password) {
  const res = await fetch(resolveMerchantPortalAdminRequestUrl("/api/kyc-admin/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || data.error || t("login.failed"));
  }
  return data;
}

async function merchantPortalAdminFetch(path, { method = "GET", body } = {}) {
  const raw = getMerchantPortalAdminToken();
  const token = raw != null ? String(raw).trim() : "";
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(resolveMerchantPortalAdminRequestUrl(path), {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
    cache: "no-store",
  });
  if (res.status === 204) return null;
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    if (res.status === 401) {
      setMerchantPortalAdminToken(null);
      if (typeof window !== "undefined" && window.location.pathname !== "/admin/login") {
        window.location.assign("/admin/login");
      }
    }
    const msg =
      (data && typeof data === "object" && (data.message || data.error)) ||
      (typeof data === "string" ? data : null) ||
      `HTTP ${res.status}`;
    const err = new Error(msg);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

export async function listMarketplaceMerchantsForPortalAdmin() {
  return merchantPortalAdminFetch("/api/kyc-admin/marketplace-merchants");
}

export async function verifyMarketplaceMerchantPortalAdmin(merchantId) {
  return merchantPortalAdminFetch(`/api/kyc-admin/marketplace-merchants/${merchantId}/verify`, {
    method: "POST",
  });
}

export async function getMarketplaceMerchantPortalAdmin(merchantId) {
  return merchantPortalAdminFetch(`/api/kyc-admin/marketplace-merchants/${merchantId}`);
}

export async function listPartnerTenantsForPortalAdmin() {
  return merchantPortalAdminFetch("/api/kyc-admin/partner-tenants");
}

export async function createPartnerTenantPortalAdmin({ name }) {
  return merchantPortalAdminFetch("/api/kyc-admin/partner-tenants", {
    method: "POST",
    body: { name },
  });
}

export async function createMarketplaceMerchantPortalAdmin(body) {
  return merchantPortalAdminFetch("/api/merchants", { method: "POST", body });
}

/** Public: always succeeds from the client's perspective (anti-enumeration); SMS only when email matches. */
export async function requestMerchantPortalForgotPassword(contactEmail) {
  const res = await api.post("/api/public/merchants/forgot-password/request", {
    contactEmail: String(contactEmail || "").trim(),
  });
  return res.data;
}

export async function confirmMerchantPortalForgotPassword(contactEmail, otp, newPassword) {
  const res = await api.post("/api/public/merchants/forgot-password/confirm", {
    contactEmail: String(contactEmail || "").trim(),
    otp: String(otp || "").trim(),
    newPassword,
  });
  return res.data;
}

export async function changeMerchantPortalPassword(merchantId, currentPassword, newPassword) {
  await api.post(`/api/merchants/${merchantId}/portal-password/change`, {
    currentPassword,
    newPassword,
  });
}
export async function listPartnerIntegrationsPortalAdmin({ merchantId, q, page = 0, size = 25 } = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  if (merchantId != null && merchantId !== "") params.set("merchantId", String(merchantId));
  if (q != null && String(q).trim() !== "") params.set("q", String(q).trim());
  return merchantPortalAdminFetch(`/api/kyc-admin/partner-integrations?${params.toString()}`);
}

export async function createPartnerCredentialsPortalAdmin({ merchant_id, label }) {
  return merchantPortalAdminFetch("/api/kyc-admin/partner-credentials", {
    method: "POST",
    body: { merchant_id, label },
  });
}

export async function revokePartnerIntegrationPortalAdmin(integrationId) {
  return merchantPortalAdminFetch(`/api/kyc-admin/partner-integrations/${integrationId}/revoke`, {
    method: "POST",
  });
}

export async function listPartnerWebhookOutboxPortalAdmin({ status, merchantId, q, page = 0, size = 25 } = {}) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("size", String(size));
  if (status) params.set("status", status);
  if (merchantId != null && merchantId !== "") params.set("merchantId", String(merchantId));
  if (q != null && String(q).trim() !== "") params.set("q", String(q).trim());
  return merchantPortalAdminFetch(`/api/kyc-admin/partner-webhook-outbox?${params.toString()}`);
}

export async function retryPartnerWebhookOutboxPortalAdmin(outboxId) {
  return merchantPortalAdminFetch(`/api/kyc-admin/partner-webhook-outbox/${outboxId}/retry`, {
    method: "POST",
  });
}
